import { Checkbox, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import {
  ApiError,
  adminCreateUserMutation,
  adminListUsersQueryKey,
  errorMessage,
  listTeamsOptions,
  schemas,
} from "@/api";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { assignableRoles } from "./userRules";

// Rules come from the generated UserCreate schema (email format, name length, role pattern).
const create = schemas.zUserCreate.shape;
const newUserSchema = z.object({
  email: create.email,
  name: create.name,
  role: create.role.unwrap().unwrap(),
  teamUuids: z.array(z.string()),
});
type NewUserValues = z.infer<typeof newUserSchema>;

function TeamChecklist({ value, onChange }: Readonly<{ value: string[]; onChange: (next: string[]) => void }>) {
  const { t } = useTranslation("admin");
  const teams = useQuery(listTeamsOptions({ query: { page_size: 100 } }));
  const choices = (teams.data?.data ?? []).filter((team) => !team.isEveryone);
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1">
        <Text as="span" variant="body-sm">
          {t("users.form.teams")}
        </Text>
      </legend>
      <Text variant="caption" tone="tertiary">
        {t("users.form.teamsHint")}
      </Text>
      {teams.isSuccess && choices.length === 0 && (
        <Text variant="body-sm" tone="tertiary">
          {t("users.form.noTeams")}
        </Text>
      )}
      {choices.map((team) => (
        <Checkbox
          key={team.uuid}
          label={team.name}
          checked={value.includes(team.uuid)}
          onChange={(checked) => onChange(checked ? [...value, team.uuid] : value.filter((id) => id !== team.uuid))}
        />
      ))}
    </fieldset>
  );
}

/** The Add user form: pre-provisions an account that links on first sign-in. */
export function NewUserForm() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const meRole = useCurrentUser().data?.user.role ?? "member";
  const createUser = useMutation(adminCreateUserMutation());

  const form = useAppForm({
    defaultValues: { email: "", name: "", role: "member", teamUuids: [] } as NewUserValues,
    validators: { onChange: newUserSchema },
    onSubmit: async ({ value }) => {
      try {
        await createUser.mutateAsync({ body: value, throwOnError: true });
        await queryClient.invalidateQueries({ queryKey: adminListUsersQueryKey() });
        toast.success(t("users.form.created", { name: value.name }));
        navigate("/admin/users");
      } catch (error) {
        toast.error(
          error instanceof ApiError && error.code === "SEAT_LIMIT_REACHED"
            ? t("users.errors.SEAT_LIMIT_REACHED")
            : errorMessage(error, t("users.form.createFailed")),
        );
      }
    },
  });

  return (
    <form
      aria-label={t("users.form.label")}
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <Text variant="body-sm" tone="secondary">
        {t("users.form.intro")}
      </Text>
      <form.AppField name="email">
        {(field) => (
          <field.TextField label={t("users.form.email")} required placeholder={t("users.form.emailPlaceholder")} />
        )}
      </form.AppField>
      <form.AppField name="name">
        {(field) => (
          <field.TextField label={t("users.form.name")} required placeholder={t("users.form.namePlaceholder")} />
        )}
      </form.AppField>
      <form.AppField name="role">
        {(field) => (
          <field.SelectField
            label={t("users.form.role")}
            options={assignableRoles(meRole).map((role) => ({ value: role, label: t(`users.roles.${role}`) }))}
          />
        )}
      </form.AppField>
      <form.Field name="teamUuids">
        {(field) => <TeamChecklist value={field.state.value} onChange={field.handleChange} />}
      </form.Field>
      <form.AppForm>
        <form.SubmitActions
          onCancel={() => navigate("/admin/users")}
          submitLabel={t("users.form.submit")}
          requireChanges={false}
        />
      </form.AppForm>
    </form>
  );
}
